import workOrderService from '@/services/workOrderService';
import { useUIStore } from '@/store/uiStore';
import { useToastStore } from '@/store/toastStore';

/**
 * Shared "weet je het zeker?" delete flow for work orders.
 * Used from the work orders overview, the planning drawer and the project page
 * so the confirmation modal is identical everywhere.
 */
export function useWorkOrderDelete() {
  const uiStore = useUIStore();
  const toastStore = useToastStore();

  /**
   * Open a confirmation modal and delete the given work order(s) on submit.
   * @param {Object|Object[]} workOrders - Work order(s) with at least an `id`
   * @param {Object} options
   * @param {Function} [options.onDeleted] - Called with the deleted ids after success
   */
  const confirmDeleteWorkOrders = (workOrders, { onDeleted } = {}) => {
    const list = (Array.isArray(workOrders) ? workOrders : [workOrders]).filter(Boolean);
    if (!list.length) return;

    const count = list.length;
    const label =
      count === 1
        ? list[0].work_order_number
          ? `werkbon ${list[0].work_order_number}`
          : 'deze werkbon'
        : `${count} werkbonnen`;

    const modalId = uiStore.addModal(false, {
      title: count === 1 ? 'Werkbon verwijderen?' : `${count} werkbonnen verwijderen?`,
      description: `Weet je zeker dat je ${label} wilt verwijderen? Dit kan niet ongedaan worden gemaakt.`,
      submitText: 'Ja, verwijder',
      cancelText: 'Terug',
      onSubmit: async () => {
        const deletedIds = [];
        let failedCount = 0;

        for (const workOrder of list) {
          try {
            await workOrderService.deleteWorkOrder(workOrder.id);
            deletedIds.push(workOrder.id);
          } catch (error) {
            console.error('Failed to delete work order:', error);
            failedCount += 1;
          }
        }

        uiStore.closeModal(modalId);

        if (deletedIds.length) {
          toastStore.addToast({
            type: 'success',
            message:
              deletedIds.length === 1
                ? 'Werkbon verwijderd'
                : `${deletedIds.length} werkbonnen verwijderd`,
          });
        }
        if (failedCount) {
          toastStore.addToast({
            type: 'error',
            message:
              failedCount === 1
                ? 'Kon werkbon niet verwijderen'
                : `Kon ${failedCount} werkbonnen niet verwijderen`,
          });
        }

        if (deletedIds.length) {
          await onDeleted?.(deletedIds);
        }
      },
      onCancel: () => uiStore.closeModal(modalId),
    });
  };

  return { confirmDeleteWorkOrders };
}
